import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { PaPermitStatus, PaPermitType, Page, ProtectedArea, ProtectedAreaPermit, ProtectedAreaQuotaResponse } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const OFFICER_ROLES = ['ENV_OFFICER'];
const RANGER_ROLES = ['PA_RANGER'];
const PA_PERMIT_TYPES: PaPermitType[] = ['ENTRY', 'FILMING', 'RESEARCH', 'CONCESSION'];
const PA_STATUSES: PaPermitStatus[] = ['PENDING', 'CONFIRMED', 'CANCELLED', 'USED', 'EXPIRED'];

type Tab = 'areas' | 'eTickets' | 'ranger';

/** Protected-area e-tickets: reference areas + quota calendar, e-ticket lifecycle, and the ranger gate check-in / permit-verify tools (M31). */
export function ProtectedAreas() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ENV_OFFICER') ?? false;
  const isRanger = persona?.roles.includes('PA_RANGER') ?? false;
  const [tab, setTab] = useState<Tab>('areas');

  return (
    <div>
      <PageHeader title="Protected Areas & E-Tickets" subtitle="Reference areas + quota calendar, e-ticket bookings, and the ranger gate (check-in / QR verify) (M31)." />
      <TabStrip
        tabs={[
          { key: 'areas', label: 'Areas & quota' },
          { key: 'eTickets', label: 'E-tickets' },
          { key: 'ranger', label: 'Ranger gate' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'areas' ? <AreasTab /> : null}
      {tab === 'eTickets' ? <ETicketsTab isOfficer={isOfficer} /> : null}
      {tab === 'ranger' ? <RangerTab isRanger={isRanger} /> : null}
    </div>
  );
}

function AreasTab() {
  const areasQ = useApiQuery<{ items: ProtectedArea[] }>('/api/v1/m31/protected-areas?active=true');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const quotaQ = useApiQuery<ProtectedAreaQuotaResponse>(selectedCode ? `/api/v1/m31/protected-areas/${selectedCode}/quota?date=${date}` : null);

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div>
        {areasQ.error ? <ErrorNote message={areasQ.error} /> : null}
        <div className="card overflow-x-auto">
          {areasQ.loading ? (
            <Spinner />
          ) : (areasQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No protected areas registered" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Code</th>
                  <th className="table-head">Name</th>
                  <th className="table-head">District</th>
                  <th className="table-head">Permit types</th>
                </tr>
              </thead>
              <tbody>
                {areasQ.data!.items.map((a) => (
                  <tr key={a.code} className={`cursor-pointer hover:bg-slate-50 ${selectedCode === a.code ? 'bg-brand-50' : ''}`} onClick={() => setSelectedCode(a.code)}>
                    <td className="table-cell font-mono text-xs font-medium">{a.code}</td>
                    <td className="table-cell">{a.name}</td>
                    <td className="table-cell text-xs text-slate-500">{a.district ?? '—'}</td>
                    <td className="table-cell text-xs text-slate-500">{a.permitTypesAllowed.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="card p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-bold text-slate-700">Quota calendar preview</div>
          <input className="field-input !w-auto !py-1" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        {!selectedCode ? (
          <EmptyState title="Select a protected area" hint="Quota is a read-only preview — it does not reserve anything." />
        ) : quotaQ.loading ? (
          <Spinner />
        ) : quotaQ.error ? (
          <ErrorNote message={quotaQ.error} />
        ) : (
          <div className="space-y-2">
            {quotaQ.data!.items.map((item) => (
              <div key={item.permitType} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                <StatusBadge status={item.permitType} />
                <span className="tabular-nums">
                  {item.issuedCount} / {item.capacity} used
                </span>
                <span className={`font-semibold tabular-nums ${item.remaining <= 0 ? 'text-rose-600' : 'text-seagreen-600'}`}>{item.remaining} left</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ETicketsTab({ isOfficer }: { isOfficer: boolean }) {
  const [status, setStatus] = useState('');
  const [showBook, setShowBook] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/m31/protected-area-permits?${params.toString()}`;
  }, [status]);
  const listQ = useApiQuery<Page<ProtectedAreaPermit>>(isOfficer ? path : null);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {PA_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button className="btn-primary" onClick={() => setShowBook(true)}>
          + Book e-ticket
        </button>
      </div>

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {isOfficer ? (
        <>
          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No e-tickets match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Type</th>
                    <th className="table-head">Visit date</th>
                    <th className="table-head">Party</th>
                    <th className="table-head">Fee</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((p) => (
                    <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                      <td className="table-cell font-mono text-xs font-medium">{p.refNo}</td>
                      <td className="table-cell">
                        <StatusBadge status={p.permitType} />
                      </td>
                      <td className="table-cell text-xs">{formatDate(p.visitDate)}</td>
                      <td className="table-cell text-xs">{p.partySize}</td>
                      <td className="table-cell text-xs">
                        {scr(p.feeAmountScr)} · <StatusBadge status={p.feeStatus} />
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={p.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {selectedId ? <ETicketDetailModal id={selectedId} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} /> : null}
      {showBook ? (
        <BookModal
          onClose={() => setShowBook(false)}
          onDone={() => {
            setShowBook(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ETicketDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<ProtectedAreaPermit>(`/api/v1/m31/protected-area-permits/${id}`);
  const p = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={p ? p.refNo : 'E-ticket'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <Detail label="Type" value={<StatusBadge status={p.permitType} />} />
            <Detail label="Status" value={<StatusBadge status={p.status} />} />
            <Detail label="Visit date" value={formatDate(p.visitDate)} />
            <Detail label="Party size" value={String(p.partySize)} />
            <Detail label="Fee" value={`${scr(p.feeAmountScr)} (${p.feeStatus})`} />
            {p.eTicketSerial ? <Detail label="E-ticket serial" value={p.eTicketSerial} mono /> : null}
          </div>
          {p.purpose ? <p className="text-xs text-slate-500">Purpose: {p.purpose}</p> : null}
          {p.cancelledReason ? <ErrorNote message={`Cancelled: ${p.cancelledReason}`} /> : null}
          {p.feeStatus !== 'PAID' ? <p className="text-xs text-amber-600">Fee confirmation + e-ticket issuance arrive via the SeyPay webhook (service-token only).</p> : null}

          {(p.status === 'PENDING' || p.status === 'CONFIRMED') ? (
            <div className="flex gap-2">
              <input className="field-input" placeholder="Cancel reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <button className="btn-danger" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m31/protected-area-permits/${id}/cancel`, { reason: reason || undefined }))}>
                Cancel
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function BookModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const areasQ = useApiQuery<{ items: ProtectedArea[] }>('/api/v1/m31/protected-areas?active=true');
  const [areaCode, setAreaCode] = useState('');
  const [permitType, setPermitType] = useState<PaPermitType>('ENTRY');
  const [visitDate, setVisitDate] = useState(() => new Date(Date.now() + 86_400_000).toISOString().slice(0, 10));
  const [partySize, setPartySize] = useState('1');
  const [purpose, setPurpose] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requiresPurpose = permitType === 'RESEARCH' || permitType === 'FILMING';

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m31/protected-area-permits', {
        areaCode,
        permitType,
        visitDate,
        partySize: Number.parseInt(partySize, 10) || 1,
        purpose: purpose || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to book e-ticket');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Book protected-area e-ticket" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Protected area">
          <select className="field-input" value={areaCode} onChange={(e) => setAreaCode(e.target.value)}>
            <option value="">Select an area…</option>
            {areasQ.data?.items.map((a) => (
              <option key={a.code} value={a.code}>
                {a.name} ({a.code})
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Permit type">
            <select className="field-input" value={permitType} onChange={(e) => setPermitType(e.target.value as PaPermitType)}>
              {PA_PERMIT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Visit date">
            <input className="field-input" type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} />
          </Field>
        </div>
        <Field label={`Party size${permitType === 'ENTRY' ? ' (visitors)' : ' (crew/activity slots — always 1 booking = 1 slot)'}`}>
          <input className="field-input" type="number" min="1" value={partySize} onChange={(e) => setPartySize(e.target.value)} />
        </Field>
        <Field label={`Purpose${requiresPurpose ? ' (required)' : ' (optional)'}`}>
          <input className="field-input" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !areaCode || (requiresPurpose && !purpose.trim())} onClick={submit}>
            {busy ? 'Booking…' : 'Book e-ticket'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface VerifyResult {
  found: boolean;
  serial?: string;
  kind?: 'ENVIRONMENTAL_PERMIT' | 'PROTECTED_AREA_PERMIT' | 'CITES_PERMIT';
  [k: string]: unknown;
}

function RangerTab({ isRanger }: { isRanger: boolean }) {
  const [checkInId, setCheckInId] = useState('');
  const [serial, setSerial] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkedIn, setCheckedIn] = useState<ProtectedAreaPermit | null>(null);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);

  const doCheckIn = async () => {
    setBusy(true);
    setError(null);
    setCheckedIn(null);
    try {
      const updated = await api.post<ProtectedAreaPermit>(`/api/v1/m31/protected-area-permits/${checkInId}/check-in`);
      setCheckedIn(updated);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Check-in failed');
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async () => {
    setBusy(true);
    setError(null);
    setVerifyResult(null);
    try {
      const result = await api.get<VerifyResult>(`/api/v1/m31/verify-permit/${encodeURIComponent(serial)}`);
      setVerifyResult(result);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Verify failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {!isRanger ? (
        <div className="lg:col-span-2">
          <RoleNotice roles={RANGER_ROLES} />
        </div>
      ) : null}
      {error ? (
        <div className="lg:col-span-2">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="card p-4">
        <div className="mb-2 text-sm font-bold text-slate-700">QR gate check-in</div>
        <p className="mb-3 text-xs text-slate-500">Scans in real deployments feed the e-ticket id straight in; paste it here for this console.</p>
        <div className="flex gap-2">
          <input className="field-input" placeholder="Protected-area permit id" value={checkInId} onChange={(e) => setCheckInId(e.target.value)} />
          <button className="btn-primary" disabled={busy || !checkInId} onClick={doCheckIn}>
            Check in
          </button>
        </div>
        {checkedIn ? (
          <div className="mt-3 rounded-lg border border-seagreen-200 bg-seagreen-50 p-3 text-sm">
            <div className="font-semibold text-seagreen-700">{checkedIn.refNo} checked in</div>
            <div className="text-xs text-slate-500">
              {checkedIn.permitType} · party of {checkedIn.partySize} · {formatDateTime(checkedIn.checkedInAt)}
            </div>
          </div>
        ) : null}
      </div>

      <div className="card p-4">
        <div className="mb-2 text-sm font-bold text-slate-700">Verify a permit serial</div>
        <p className="mb-3 text-xs text-slate-500">Fans in across environmental permits, protected-area e-tickets, and CITES permits — one lookup for any issued serial.</p>
        <div className="flex gap-2">
          <input className="field-input" placeholder="Serial number" value={serial} onChange={(e) => setSerial(e.target.value)} />
          <button className="btn-secondary" disabled={busy || !serial} onClick={doVerify}>
            Verify
          </button>
        </div>
        {verifyResult ? (
          <div className={`mt-3 rounded-lg border p-3 text-xs ${verifyResult.found ? 'border-seagreen-200 bg-seagreen-50' : 'border-rose-200 bg-rose-50'}`}>
            {verifyResult.found ? (
              <pre className="whitespace-pre-wrap font-mono text-[11px] text-slate-700">{JSON.stringify(verifyResult, null, 2)}</pre>
            ) : (
              <span className="text-rose-700">No permit found for that serial.</span>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
